// ChunckedTask

import Resumable from 'resumablejs';
import { getHeaders } from 'src/relay/helpers';
import config from './config';
import { uuid } from './helpers';

export type ChunkedTaskCreateUrl = 'imagetasks' | 'shapetasks' | 'areatemplatetasks';
export type ChunkedTaskResultUrl = 'imageTempFileTaskResult' | 'imagetasks' | 'shapetasks' | 'areatemplatetasks';

export class ChunkedTask {
    constructor(
        public createTasksUrl: ChunkedTaskCreateUrl,
        public resultTasksUrl: ChunkedTaskResultUrl
    ) {}

    public upload = async (file: File, options: any, onProgress?: (percentage: number) => void) => {
        return await this.uploadFileChunked(file, file.type, options.customFileID ?? uuid(), options, onProgress);
    };

    private uploadFileChunked = async (imageFile: File, mimeType: string, fileID: string, options: any, onProgress?: (percentage: number) => void) => {
        let status = await resumableUpload(imageFile, mimeType, fileID, onProgress);

        if (status === 'OK') {
            let taskID = await this.createTask(fileID, options);

            if (taskID !== -1) {
                let responseTaskImage = await this.getResultFromTask(taskID);
                if (responseTaskImage) {
                    return responseTaskImage;
                }
            } else {
                throw 'Upload failed';
            }
        }

        return null;
    };

    private createTask = async (fileID: string, options: any) => {
        let imageTask = { fileID: fileID, options: options };
        let response = await fetch(config.imageTasksApiUrl + this.createTasksUrl, {
            credentials: 'include',
            headers: getHeaders(null),
            method: 'POST',
            body: JSON.stringify(imageTask)
        });

        return response && response.status == 200 ? (await response.json()).taskID : -1;
    };

    private getResultFromTask = async (taskID: string): Promise<any | null> => {
        let times = 0;

        for (;;) {
            try {
                const response = await fetch(config.imageTasksApiUrl + this.resultTasksUrl + '/results/' + taskID, {
                    credentials: 'include',
                    headers: getHeaders(null)
                });
                const json = await response.json();

                if (json && json.state === 2) {
                    return json;
                } else if (json && json.state === 3) {
                    return null;
                }
            } catch (ex) {
                return null;
            }

            times++;
            if (times > 100) {
                return null;
            }

            // Delay 1s
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    };
}

export const resumableUpload = async (file: File, mimeType: string, fileID: string, onProgress?: (percentage: number) => void) => {
    return new Promise((resolve, reject) => {
        // Usa la classe Resumable originale per evitare conflitti con il wrapper globale
        const ResumableClass = (window as any).OriginalResumable || Resumable;

        let r = new ResumableClass({
            target: config.imagesUploadApiUrl + 'filechunks/upload',
            generateUniqueIdentifier: function () {
                return fileID;
            },
            maxFiles: 1,
            testChunks: false,
            headers: { Authorization: 'Bearer ' + config.token },
            query: { resumableType: mimeType },
            simultaneousUploads: 3
        });

        // Adding file
        r.addFile(file);

        // Event Upload handlers
        r.on('fileError', function (file: any, message: any) {
            resolve('KO');
            console.error('fileError', file, message);
        });

        r.on('error', function (message: any, file: any) {
            resolve('KO');
            console.error('error', message, file);
        });

        r.on('progress', function () {
            let percentage = r.progress() * 100;

            if (onProgress) onProgress(percentage);
        });

        r.on('fileAdded', function (file: any, event: any) {
            r.upload();
        });

        r.on('complete', function () {
            resolve('OK');
        });
    });
};
